import { ThemeProvider } from '@mui/material/styles';
import { Stack } from '@mui/material';

// Styles
import './App.scss';
import '../../common/styles/typography.scss';
import '../../common/styles/variables.scss';
import '../../common/styles/common.scss';
import theme from '../../common/styles/newStyles/LelyMuiTheme';

// Components
import Header from '../Header/Header';
import TopLevelRouter from './TopLevelRouter';
import Preloader from '../Preloader/Preloader';
import {
  ErrorBoundary,
  AppNavigation,
  SnackbarsContainer,
} from '../../common/components';

// Redux
import { useAppSelector } from '../../reducers/rootReducer';
import { selectInterceptorsStatus } from '../../reducers/interceptorsSlice';

// Constants
import {
  ADE_ONLINE_UPDATES_FEATURE_FLAG,
  BACKUP_AND_RESTORE,
  BACKUP_VERSION_FF,
  DIAGNOSTIC_PAGE,
  ALARM_CARDS_FF,
  PERSISTENT_SNACKBAR,
  TEMPORARY_SNACKBAR,
  EVENTS_HEATMAP_PAGE_FF,
  AMF_DATA_EXPLORER_PAGE_FF,
  N_CAPTURE_VISIBILITY_FF,
  ASTRONAUT_MAX_VISIBILITY_FF,
  RING_BUFFER_FF,
} from '../../common/constants';

// Custom hooks
import {
  useMopinion,
  useErrorHandling,
  useErrorStatus,
  useSnackbarFromFeatureFlag,
} from '../../common/customHooks';
import {
  useAlarmIdRepresentation,
  useAuth0PostLoginActions,
  useBddManualLogin,
  useBrowserSpecificExceptions,
  useDisableGlobalPinchZoom,
  useFeatureFlag,
  useGoogleAnalytics,
  useInvalidateAppCache,
  useLoginWithRedirect,
  usePreferredSystemsHydration,
  useRedirectListener,
} from './hooks';
import RouterErrorBoundary from '../RouterErrorBoundary/RouterErrorBoundary';

export const featureFlags = [
  ADE_ONLINE_UPDATES_FEATURE_FLAG,
  DIAGNOSTIC_PAGE,
  BACKUP_AND_RESTORE,
  BACKUP_VERSION_FF,
  ALARM_CARDS_FF,
  PERSISTENT_SNACKBAR,
  TEMPORARY_SNACKBAR,
  RING_BUFFER_FF,
  EVENTS_HEATMAP_PAGE_FF,
  AMF_DATA_EXPLORER_PAGE_FF,
  N_CAPTURE_VISIBILITY_FF,
  ASTRONAUT_MAX_VISIBILITY_FF,
];

function App() {
  const interceptorsStatus = useAppSelector(selectInterceptorsStatus);

  useErrorStatus();
  useAlarmIdRepresentation();
  useSnackbarFromFeatureFlag();
  useInvalidateAppCache();
  useGoogleAnalytics();
  useDisableGlobalPinchZoom();
  useRedirectListener();
  useFeatureFlag(interceptorsStatus);
  useMopinion();
  useErrorHandling();
  useBrowserSpecificExceptions();

  useLoginWithRedirect();
  useAuth0PostLoginActions();
  usePreferredSystemsHydration();
  useBddManualLogin();

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        {interceptorsStatus === 'not-initialized' ? (
          <Preloader activeByDefault />
        ) : (
          <>
            <SnackbarsContainer />
            <Stack direction="row" sx={{ height: '100vh' }}>
              <AppNavigation />
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Header />
                <RouterErrorBoundary>
                  <TopLevelRouter />
                </RouterErrorBoundary>
              </Stack>
            </Stack>
          </>
        )}
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
